/**
 * `UserMenu` — controllo di sessione nella navbar.
 *
 * Mostra l'avatar con le iniziali, il nome dell'utente e un menu a comparsa con
 * email, badge di ruolo e azione di logout. Consuma `useAuth()`:
 *  - identità: `me.displayName ?? me.email ?? user.profile.name`;
 *  - ruolo: `role` (autorevole da `/api/me`, con fallback ai claim OIDC);
 *  - `logout()` per terminare la sessione.
 *
 * Quando non c'è sessione mostra un pulsante "Accedi" che avvia `login()`.
 *
 * Accessibilità: il trigger espone `aria-haspopup="menu"` e `aria-expanded`; il
 * pannello ha `role="menu"` e le voci `role="menuitem"`. Si chiude su Escape e
 * su click esterno.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import type { Role } from '../shared/dto';
import { Badge } from './ui';
import type { BadgeTone } from './ui';

/** Voce di navigazione del menu utente. */
interface MenuItem {
  to: string;
  label: string;
  /**
   * Se `true`, la voce è riservata all'admin e non è mostrata al manager (pur
   * restando concettualmente nella sezione "Gestione"). Coerente con le guardie
   * di rotta: `/admin/jobs` e `/admin/certifications` sono solo-admin.
   */
  adminOnly?: boolean;
}

/**
 * Voci di **Gestione**: catalogo del dominio (skill, competenze, professioni,
 * certificazioni). Skill e Competenze sono aperte anche al manager; Professioni
 * e Certificazioni restano riservate all'admin (`adminOnly`).
 */
const MANAGEMENT_ITEMS: MenuItem[] = [
  { to: '/admin/skills', label: 'Skill' },
  { to: '/admin/competences', label: 'Competenze' },
  { to: '/admin/jobs', label: 'Professioni', adminOnly: true },
  { to: '/admin/certifications', label: 'Certificazioni', adminOnly: true },
];

/**
 * Voci di **Amministrazione**: riservate all'admin (organizzazione, utenti e
 * configurazioni di calcolo dello scoring).
 */
const ADMIN_ITEMS: MenuItem[] = [
  { to: '/admin/org', label: 'Organigramma' },
  { to: '/admin/users', label: 'Utenti' },
  { to: '/admin/scoring', label: 'Configurazioni' },
];

/** Etichette leggibili dei ruoli applicativi. */
const ROLE_LABEL: Record<Role, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  reviewer: 'Reviewer',
  employer: 'Dipendente',
};

const ROLE_TONE: Record<Role, BadgeTone> = {
  admin: 'primary',
  manager: 'info',
  reviewer: 'success',
  employer: 'neutral',
};

/** Iniziali da un nome/email per l'avatar (max 2 caratteri). */
function initials(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return trimmed.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

export function UserMenu() {
  const { isAuthenticated, me, user, role, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // Chiude su click esterno ed Escape mentre il pannello è aperto.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        className="ui-btn ui-btn--primary"
        onClick={() => {
          void login().catch((err) =>
            console.error('UserMenu: avvio login non riuscito', err),
          );
        }}
      >
        Accedi
      </button>
    );
  }

  const profileName = (user?.profile?.name as string | undefined) ?? undefined;
  const displayName = me?.displayName || profileName || me?.email || 'Utente';
  const email = me?.email ?? (user?.profile?.email as string | undefined) ?? '';

  const onLogout = () => {
    close();
    void logout().catch((err) => console.error('UserMenu: logout non riuscito', err));
  };

  return (
    <div className="user-menu" ref={containerRef}>
      <button
        type="button"
        className="ui-btn ui-btn--ghost user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-menu__avatar" aria-hidden="true">
          {initials(displayName)}
        </span>
        <span className="user-menu__name">{displayName}</span>
        {/* Freccia piena disegnata in CSS: ruota verso l'alto quando il
            pannello è aperto (stato guidato da `aria-expanded` sul trigger). */}
        <span className="user-menu__caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="user-menu__panel" role="menu" aria-label="Menu utente">
          <div className="user-menu__header">
            <div className="user-menu__display">{displayName}</div>
            {email && <div className="user-menu__email">{email}</div>}
            {role && (
              <div style={{ marginTop: 'var(--space-2)' }}>
                <Badge tone={ROLE_TONE[role]}>{ROLE_LABEL[role]}</Badge>
              </div>
            )}
          </div>

          {/* Voce Profilo: impostazioni personali (Credly, ecc.), per tutti. */}
          <div className="user-menu__divider" role="separator" />
          <NavLink
            to="/profile"
            role="menuitem"
            className="ui-btn user-menu__item"
            onClick={close}
          >
            Profilo
          </NavLink>

          {/* La voce "Il mio CV" è stata rimossa dal menu: la pagina ora
              coincide con la "dashboard" della sezione Assessment (`/assessment`,
              voce di navbar "Dashboard"), di cui è un superset. */}

          {/* Sezione "Gestione": catalogo del dominio. Il manager vede solo le
              voci non `adminOnly` (Skill, Competenze); l'admin le vede tutte. */}
          {(role === 'admin' || role === 'manager') &&
            (() => {
              const items =
                role === 'admin'
                  ? MANAGEMENT_ITEMS
                  : MANAGEMENT_ITEMS.filter((item) => !item.adminOnly);
              if (items.length === 0) return null;
              return (
                <>
                  <div className="user-menu__divider" role="separator" />
                  <div className="user-menu__section-label">
                    <span aria-hidden="true">Gestione</span>
                  </div>
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className="ui-btn user-menu__item"
                      onClick={close}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </>
              );
            })()}

          {/* Sezione "Amministrazione": riservata all'admin. Include
              "Configurazioni" (parametri di calcolo). */}
          {role === 'admin' && (
            <>
              <div className="user-menu__divider" role="separator" />
              <div className="user-menu__section-label">
                <span aria-hidden="true">Amministrazione</span>
              </div>
              {ADMIN_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className="ui-btn user-menu__item"
                  onClick={close}
                >
                  {item.label}
                </NavLink>
              ))}
            </>
          )}

          <div className="user-menu__divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="ui-btn user-menu__item"
            onClick={onLogout}
          >
            Esci
          </button>
        </div>
      )}
    </div>
  );
}
